// PermitInfoComponent.js
import { useState, useEffect } from "react";
import {
  EditableInputComponent,
  EditableSelectComponent,
  EditableTextareaComponent,
  SectionHeader,
} from "components";
import { Document } from "assets/icons"; // Replace with appropriate icon
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import PermitsComponent from "./permitsComponent";
import {
  BuildingRegulatoryInfoDTO,
  BuildingVolumePermitStatus,
  UnitsPermittedStatus,
} from "shared/types/regulatory-info";
import { UnitDTO } from "shared/types/unit";
import { BuildingDTO } from "shared/types/building";
import { FileLabel } from "shared/types/file";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";

interface PermitInfoComponentProps {
  buildingRegulatoryInfo: BuildingRegulatoryInfoDTO;
  handlePermitsInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  parentEditMode?: boolean;
  unit: UnitDTO | undefined | null;
  building: BuildingDTO | undefined | null;
}

const PermitInfoComponent = ({
  buildingRegulatoryInfo,
  handleCloseParent,
  handleSaveInParent,
  handlePermitsInfoChangeInParent,
  parentEditMode = true,
  unit,
  building,
}: PermitInfoComponentProps) => {
  const [editMode, setEditMode] = useState(parentEditMode);
  const [editedBuildingRegulatoryInfo, setEditedBuildingRegulatoryInfo] =
    useState(buildingRegulatoryInfo);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { session } = useSession();
  const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
  const [selectedFileLabel, setSelectedFileLabel] = useState<FileLabel | null>(
    null,
  );

  useEffect(() => {
    setEditedBuildingRegulatoryInfo(buildingRegulatoryInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch

  const handleSave = async () => {
    setHasUnsavedChanges(false);

    try {
      if (handleSaveInParent) {
        handleSaveInParent();
      } else {
        await fetchWithAuth(
          `${API_URL}/building/permit-info`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(editedBuildingRegulatoryInfo),
          },
          session,
        );

        toast.success("Vergunningsinformatie succesvol bijgewerkt!");
      }
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het bijwerken van de vergunningsinformatie.",
      );
    }
  };

  const handleEditClick = () => {
    setEditMode(!editMode);
  };

  const updateBuildingRegulatoryInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedBuildingRegulatoryInfo((prev: any) => ({
      ...prev,
      ...updatedFields,
    }));

    if (handlePermitsInfoChangeInParent) {
      handlePermitsInfoChangeInParent({
        ...buildingRegulatoryInfo,
        ...updatedFields,
      });
    }
  };

  const handleFileUploadSuccess = async (fileLogData: any) => {
    const { fileLog } = fileLogData;

    let updatedField = {};

    switch (selectedFileLabel) {
      case FileLabel.StedenbouwkundigeInlichtingen:
        updatedField = {
          urbanExtractFileLogId: fileLog.id,
          urbanExtractFileLog: fileLog,
        };
        break;
      // Add more cases as needed
      default:
        return;
    }

    const updatedBuildingPermitsInfo = {
      ...buildingRegulatoryInfo,
      ...updatedField,
    };

    // Save the updated information to the backend
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/permit-info`,
        {
          method: "POST", // or POST, depending on your API
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedBuildingPermitsInfo),
        },
        session,
      );

      if (response.ok) {
        toast.success("Vergunningsinformatie succesvol geupdate");
        setHasUnsavedChanges(false); // Reset unsaved changes state
        setEditedBuildingRegulatoryInfo(updatedBuildingPermitsInfo); // Update local state
      } else {
        throw new Error("Failed to update Permits information");
      }
    } catch (error) {
      console.error("Error updating Permits information:", error);
      toast.error("Failed to update Permits information");
    }
  };

  const handleFileModalClose = async () => {
    setSelectedFileLabel(null);
    setFileModalOpen(false);
  };

  const connectFileLogToPermitsInfo = (fileLogData: any) => {
    const { fileLog } = fileLogData;

    if (selectedFileLabel === FileLabel.StedenbouwkundigeInlichtingen) {
      updateBuildingRegulatoryInfo({ urbanExtractFileLogId: fileLog.id });

      setSelectedFileLabel(null); // finally reset the selected file label
    }
  };

  const getStatusColor = (status: any) => {
    switch (status) {
      case BuildingVolumePermitStatus.OK:
      case BuildingVolumePermitStatus.NOT_REQUIRED:
      case UnitsPermittedStatus.OK:
      case UnitsPermittedStatus.NOT_REQUIRED:
        return "bg-green-500"; // Green for OK
      case BuildingVolumePermitStatus.PARTIAL:
        return "bg-orange-500"; // Yellow for Partial
      case BuildingVolumePermitStatus.NOT_OK:
        return "bg-red-500"; // Red for Not OK
      case BuildingVolumePermitStatus.UNKNOWN:
      default:
        return "bg-transparent-500";
    }
  };

  return (
    <section className="flex flex-col gap-0 rounded-md bg-white">
      <SectionHeader
        title="Vergunning informatie"
        icon={<Document />}
        editMode={editMode}
        onEditClick={handleEditClick}
        onSaveClick={handleSave}
        sticky={false}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={() => {
          if (handleCloseParent) handleCloseParent();
        }}
        hideCloseAction={true}
      />
      <div className="p-3">
        <div className="gap-3 sm:grid sm:grid-cols-2">
          {/* Building Volume Permitted */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedBuildingRegulatoryInfo &&
                    editedBuildingRegulatoryInfo?.buildingVolumePermitted,
                )}`}
              />
              <p>Gebouwvolume vergund?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze"
              value={
                (editedBuildingRegulatoryInfo &&
                  editedBuildingRegulatoryInfo?.buildingVolumePermitted) ||
                ""
              }
              isLoading={false}
              options={Object.values(BuildingVolumePermitStatus).map(
                (value) => ({ label: value, value }),
              )}
              editMode={editMode}
              onChange={(value) =>
                updateBuildingRegulatoryInfo({ buildingVolumePermitted: value })
              }
            />
          </div>
          {/* Building Permit Remarks */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
              <p>Gebouw vergunningstatus opmerkingen</p>
            </header>
            <EditableTextareaComponent
              label="Opmerkingen"
              value={
                (editedBuildingRegulatoryInfo &&
                  editedBuildingRegulatoryInfo?.buildingPermitRemarks) ||
                ""
              }
              isLoading={false}
              editMode={editMode}
              onChange={(value) =>
                updateBuildingRegulatoryInfo({ buildingPermitRemarks: value })
              }
            />
          </div>
          {/* Units Permitted */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedBuildingRegulatoryInfo &&
                    editedBuildingRegulatoryInfo?.unitsPermitted,
                )}`}
              />
              <p>Zijn alle units vergund?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze"
              value={
                (editedBuildingRegulatoryInfo &&
                  editedBuildingRegulatoryInfo?.unitsPermitted) ||
                ""
              }
              isLoading={false}
              options={Object.values(UnitsPermittedStatus).map((value) => ({
                label: value,
                value,
              }))}
              editMode={editMode}
              onChange={(value) =>
                updateBuildingRegulatoryInfo({ unitsPermitted: value })
              }
            />
          </div>
          {/* Unit Permit Remarks */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
              <p>Unit vergunningstatus opmerkingen</p>
            </header>
            <EditableTextareaComponent
              label="Opmerkingen"
              value={
                (editedBuildingRegulatoryInfo &&
                  editedBuildingRegulatoryInfo?.unitPermitRemarks) ||
                ""
              }
              isLoading={false}
              editMode={editMode}
              onChange={(value) =>
                updateBuildingRegulatoryInfo({ unitPermitRemarks: value })
              }
            />
          </div>
          {/* Number of Units Present */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
              <p>Aantal units aanwezig</p>
            </header>
            <EditableInputComponent
              label="Aantal"
              value={
                (editedBuildingRegulatoryInfo &&
                  (editedBuildingRegulatoryInfo?.numUnitsPresent as any)) ||
                ""
              }
              editMode={editMode}
              isLoading={false}
              onChange={(value) =>
                updateBuildingRegulatoryInfo({ numUnitsPresent: value })
              }
            />
          </div>
          {/* Number of Units Permitted */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
              <p>Aantal units vergund</p>
            </header>
            <EditableInputComponent
              label="Aantal"
              value={
                (editedBuildingRegulatoryInfo &&
                  (editedBuildingRegulatoryInfo?.numUnitsPermitted as any)) ||
                ""
              }
              editMode={editMode}
              isLoading={false}
              onChange={(value) =>
                updateBuildingRegulatoryInfo({ numUnitsPermitted: value })
              }
            />
          </div>
        </div>
        {editedBuildingRegulatoryInfo && (
          <PermitsComponent
            buildingRegulatoryInfoId={editedBuildingRegulatoryInfo?.id}
            unit={unit}
            building={building}
          />
        )}
        <ModalUploadFileLog
          isOpen={fileModalOpen}
          onClose={handleFileModalClose}
          onSave={connectFileLogToPermitsInfo}
          customLabels={[FileLabel.StedenbouwkundigeInlichtingen, "Watertoets", "Erfgoedinfo", "PV overtreding stedenbouw", FileLabel.Omgevingsvergunning, FileLabel.Milieuvergunning, FileLabel.ConformiteitsattestStedenbouw]}
          unit={unit}
          building={building}
          onFileUploadSuccess={handleFileUploadSuccess}
        />
      </div>
    </section>
  );
};

export default PermitInfoComponent;
